import React, { useState } from 'react';
import { fmtMoney } from './format';
import { yearTotals } from '../../utils/rollup';

// The MasterFiles view: one row per fiscal year, what was worked, what was
// billed and how, and what is left to bill. Rows are utils/rollup.js byYear()'s
// -- this file adds nothing up except through yearTotals().
//
// Used twice: Summary by Year on the client / group / task pages (every
// column, cells clickable), and the WIP panel on Create Invoice (the short set
// of columns by default, rows expandable to the records behind them).

const COLUMNS = [
  { key: 'hours', label: 'Hours', fmt: (v) => (Number(v) || 0).toFixed(2), drill: 'time', metric: 'hours', short: false },
  { key: 'time', label: 'Time value', drill: 'time', metric: 'time', short: true },
  { key: 'feesBilled', label: 'Fees billed', drill: 'invoice', metric: 'invoiced', short: true },
  { key: 'discount', label: 'Discount', drill: 'invoice', metric: 'invoiced', short: true },
  { key: 'invoiced', label: 'Net fees', drill: 'invoice', metric: 'invoiced', short: false },
  { key: 'disbursements', label: 'Disb.', drill: 'invoice', metric: 'invoiced', short: false },
  { key: 'tax', label: 'Tax', drill: 'invoice', metric: 'invoiced', short: false },
  { key: 'total', label: 'Total invoiced', drill: 'invoice', metric: 'invoiced', short: false },
  { key: 'writeoffs', label: 'Write-offs', drill: 'writeoff', metric: 'writeoffs', short: true },
  { key: 'wip', label: 'WIP left', drill: 'wip', metric: 'wip', short: true },
];

const NO_WIP_TITLE = 'Before time was recorded in ClientZone -- these invoices bill work it never saw, so they do not count against WIP';

const th = {
  padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600,
  color: '#374151', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
};
const td = { padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' };

const drillRowsOf = (r, drill) => {
  if (drill === 'time') return r.timeEntries;
  if (drill === 'invoice') return r.invoices;
  if (drill === 'writeoff') return r.writeOffEntries;
  return { timeEntries: r.timeEntries, invoices: r.invoices, writeOffs: r.writeOffEntries };
};
const hasRows = (rows) => (Array.isArray(rows)
  ? rows.length > 0
  : rows.timeEntries.length + rows.invoices.length + rows.writeOffs.length > 0);

const isUnfiled = (r) => r.year === null || r.year === undefined;
const yearLabel = (r) => (isUnfiled(r) ? 'No fiscal year' : `FY ${r.year}`);

const wipColor = (v) => (v < 0 ? '#dc2626' : '#b45309');

export default function BillingByYearTable({
  rows, onCellClick, short = false, allowToggle = false, renderDetail, fontSize = 14,
}) {
  const [showAll, setShowAll] = useState(!short);
  const [open, setOpen] = useState({});
  const columns = showAll ? COLUMNS : COLUMNS.filter((c) => c.short);
  const totals = yearTotals(rows);

  const display = (c, v) => {
    if (c.key === 'wip' && (v === null || v === undefined)) {
      return <span title={NO_WIP_TITLE} style={{ color: '#9ca3af' }}>—</span>;
    }
    if (c.fmt) return c.fmt(v);
    return fmtMoney(v);
  };

  const cell = (c, r) => {
    const v = r[c.key];
    const drillRows = drillRowsOf(r, c.drill);
    const clickable = Boolean(onCellClick) && hasRows(drillRows)
      && !(c.key === 'wip' && (v === null || v === undefined));
    const color = c.key === 'wip' && v !== null && v !== undefined ? wipColor(v) : undefined;
    return (
      <td
        key={c.key}
        data-col={c.key}
        style={{
          ...td,
          color,
          fontWeight: c.key === 'wip' ? 600 : 400,
          cursor: clickable ? 'pointer' : 'default',
        }}
        onClick={clickable ? (e) => {
          e.stopPropagation();
          onCellClick({ year: r.year, metric: c.metric, kind: c.drill, rows: drillRows });
        } : undefined}
      >
        {display(c, v)}
      </td>
    );
  };

  const toggleRow = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div>
      {allowToggle ? (
        <div style={{ textAlign: 'right', marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            style={{
              background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer',
              fontSize: 12, padding: 0,
            }}
          >
            {showAll ? 'Fewer columns' : 'All columns'}
          </button>
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table
          data-testid="billing-by-year"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize }}
        >
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...th, textAlign: 'left' }}>Fiscal year</th>
              {columns.map((c) => <th key={c.key} style={th}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => {
              const key = isUnfiled(r) ? '__unfiled__' : String(r.year);
              const expandable = Boolean(renderDetail);
              const isOpen = Boolean(open[key]);
              return (
                <React.Fragment key={key}>
                  <tr
                    data-testid={`billing-year-${key}`}
                    onClick={expandable ? () => toggleRow(key) : undefined}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      cursor: expandable ? 'pointer' : 'default',
                    }}
                  >
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                      {expandable ? (
                        <span style={{ color: '#6b7280', fontSize: 10, marginRight: 6 }}>
                          {isOpen ? '▼' : '▶'}
                        </span>
                      ) : null}
                      {yearLabel(r)}
                    </td>
                    {columns.map((c) => cell(c, r))}
                  </tr>
                  {expandable && isOpen ? (
                    <tr data-testid={`billing-detail-${key}`}>
                      <td colSpan={columns.length + 1} style={{ padding: '0 0 8px 16px' }}>
                        {renderDetail(r)}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
          {(rows || []).length > 1 ? (
            <tfoot>
              <tr data-testid="billing-total" style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
                <td style={{ ...td, textAlign: 'left' }}>Total</td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{ ...td, color: c.key === 'wip' ? wipColor(totals.wip) : undefined }}
                  >
                    {c.fmt ? c.fmt(totals[c.key]) : fmtMoney(totals[c.key])}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
